import React, { useEffect, useState } from 'react';
import { api, uploadFile } from '../shared/api';
import { formatMoney } from '../shared/formatting';

type Coverage = 'live' | 'partial' | 'manual' | 'missing';

type Overview = {
    dates: string[]; // 'YYYY-MM-DD' ascending
    latest: string;
    prev: string | null;
    lines: Array<{
        line_key: string;
        rbr: string | null;
        description: string;
        section: string | null;
        is_derived: boolean;
        sort_order: number;
        values: Record<string, number | null>;
        latest_value: number | null;
        prev_value: number | null;
        delta: number | null;
    }>;
    gap_trend: Array<{ date: string; value: number | null }>;
    bis_auto: {
        computed_at: string;
        lines: Array<{ line_key: string; label: string; section: string | null; amount: number | null; coverage: Coverage; note: string }>;
        bis_gap_partial: number | null;
        coverage_note: string;
    };
    reconcile: Array<{ line_key: string; label: string; report: number | null; bis: number; delta: number | null; coverage: Coverage }>;
    source_file: string | null;
};

const COVERAGE_META: Record<Coverage, { color: string; icon: string; hr: string; en: string }> = {
    live: { color: '#1f7a36', icon: '✅', hr: 'Uživo', en: 'Live' },
    partial: { color: '#b8860b', icon: '🟡', hr: 'Djelomično', en: 'Partial' },
    manual: { color: '#c2760c', icon: '🟠', hr: 'Ručno', en: 'Manual' },
    missing: { color: '#b14040', icon: '🔴', hr: 'Nedostaje', en: 'Missing' },
};

function CoverageBadge({ coverage, locale }: { coverage: Coverage; locale: 'hr' | 'en' }) {
    const meta = COVERAGE_META[coverage];
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: '0.7rem',
                fontWeight: 600,
                lineHeight: 1.4,
                color: meta.color,
                background: `${meta.color}18`,
                border: `1px solid ${meta.color}44`,
                whiteSpace: 'nowrap',
            }}
        >
            <span aria-hidden>{meta.icon}</span>
            {locale === 'en' ? meta.en : meta.hr}
        </span>
    );
}

const SECTION_LABELS: Record<string, { hr: string; en: string }> = {
    cash: { hr: 'Novac', en: 'Cash' },
    goods: { hr: 'Roba', en: 'Goods' },
    obligations: { hr: 'Obveze', en: 'Obligations' },
    result: { hr: 'Rezultat', en: 'Result' },
    memo: { hr: 'Memo', en: 'Memo' },
};

export function GapReportModule({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const [overview, setOverview] = useState<Overview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const dmy = (iso: string) => iso.split('-').reverse().join('.');

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError('');
        api<Overview>('/gap-report/overview', token)
            .then((res) => {
                if (alive) {
                    setOverview(res);
                }
            })
            .catch((e) => {
                if (alive) {
                    setError(e instanceof Error ? e.message : 'API');
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [token]);

    async function submitUpload() {
        if (!file) {
            return;
        }
        setUploading(true);
        setError('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await uploadFile<Overview>('/gap-report/upload', token, fd);
            setOverview(res);
            setFile(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : t('Greška pri uvozu', 'Upload error'));
        } finally {
            setUploading(false);
        }
    }

    const cardStyle: React.CSSProperties = {
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 12,
        padding: 16,
        background: '#fff',
    };
    const mutedLabel: React.CSSProperties = {
        color: '#8a90a6',
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    };
    const accentBg = 'rgba(var(--ad-acc-rgb, 47, 122, 54), 0.08)';

    function money(v: number | null): string {
        return v === null || v === undefined ? '—' : formatMoney(v, locale);
    }

    function DeltaCell({ value }: { value: number | null }) {
        if (value === null || value === undefined) {
            return <span style={{ color: '#8a90a6' }}>—</span>;
        }
        if (value > 0) {
            return <span style={{ color: '#1f7a36', fontWeight: 600 }}>▲ {formatMoney(value, locale)}</span>;
        }
        if (value < 0) {
            return <span style={{ color: '#b14040', fontWeight: 600 }}>▼ {formatMoney(value, locale)}</span>;
        }
        return <span style={{ color: '#8a90a6' }}>{formatMoney(value, locale)}</span>;
    }

    if (loading) {
        return <p style={{ color: '#8a90a6' }}>{t('Učitavam…', 'Loading…')}</p>;
    }

    if (error && !overview) {
        return <p style={{ color: '#b14040' }}>{error}</p>;
    }

    if (!overview) {
        return <p style={{ color: '#8a90a6' }}>{t('Nema podataka.', 'No data.')}</p>;
    }

    const trendMax = Math.max(1, ...overview.gap_trend.map((g) => (g.value === null ? 0 : Math.abs(g.value))));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {error ? <p style={{ color: '#b14040', margin: 0 }}>{error}</p> : null}

            {/* 1) Upload row */}
            <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        disabled={uploading}
                        onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                        style={{ fontSize: '0.85rem' }}
                    />
                    <button
                        type="button"
                        onClick={() => void submitUpload()}
                        disabled={uploading || !file}
                        style={{
                            padding: '8px 16px',
                            borderRadius: 8,
                            border: '1px solid var(--ad-acc)',
                            background: uploading || !file ? '#e9ebf1' : 'var(--ad-acc)',
                            color: uploading || !file ? '#8a90a6' : '#fff',
                            fontWeight: 600,
                            cursor: uploading || !file ? 'default' : 'pointer',
                            fontSize: '0.85rem',
                        }}
                    >
                        {uploading ? t('Uvozim…', 'Importing…') : t('Uvezi GAP xlsx', 'Import GAP xlsx')}
                    </button>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={mutedLabel}>{t('Izvor', 'Source')}</span>
                    <span style={{ color: 'var(--ad-ink)', fontWeight: 600, fontSize: '0.9rem' }}>
                        {overview.source_file ?? t('—', '—')}
                    </span>
                    <span style={{ color: '#8a90a6', fontSize: '0.8rem' }}>
                        {t('Zadnji', 'Latest')}: {dmy(overview.latest)}
                    </span>
                </div>
            </div>

            {/* 2) GAP trend */}
            <div style={cardStyle}>
                <div style={{ ...mutedLabel, marginBottom: 12 }}>{t('GAP kroz vrijeme', 'GAP over time')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {overview.gap_trend.map((g) => {
                        const pct = g.value === null ? 0 : (Math.abs(g.value) / trendMax) * 100;
                        const positive = g.value !== null && g.value >= 0;
                        return (
                            <div key={g.date} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ width: 90, flexShrink: 0, color: '#8a90a6', fontSize: '0.8rem' }}>{dmy(g.date)}</span>
                                <div style={{ flex: 1, height: 18, background: '#f2f3f7', borderRadius: 4, overflow: 'hidden' }}>
                                    <div
                                        style={{
                                            width: `${pct}%`,
                                            height: '100%',
                                            background: positive ? '#1f7a36' : '#b14040',
                                            borderRadius: 4,
                                            transition: 'width 0.2s',
                                        }}
                                    />
                                </div>
                                <span
                                    style={{
                                        width: 130,
                                        flexShrink: 0,
                                        textAlign: 'right',
                                        fontSize: '0.85rem',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: g.value === null ? '#8a90a6' : positive ? '#1f7a36' : '#b14040',
                                        fontWeight: 600,
                                    }}
                                >
                                    {g.value === null ? '—' : formatMoney(g.value, locale)}
                                </span>
                            </div>
                        );
                    })}
                    {overview.gap_trend.length === 0 ? (
                        <span style={{ color: '#8a90a6', fontSize: '0.85rem' }}>{t('Nema trend podataka.', 'No trend data.')}</span>
                    ) : null}
                </div>
            </div>

            {/* 3) Breakdown matrix */}
            <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
                <div style={{ ...mutedLabel, padding: '16px 16px 8px' }}>{t('Raščlamba', 'Breakdown')}</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ textAlign: 'left' }}>
                            <th style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.08)', ...mutedLabel, fontSize: '0.68rem' }}>
                                {t('Stavka', 'Line')}
                            </th>
                            {overview.dates.map((d) => {
                                const isLatest = d === overview.latest;
                                return (
                                    <th
                                        key={d}
                                        style={{
                                            padding: '8px 12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                                            textAlign: 'right',
                                            ...mutedLabel,
                                            fontSize: '0.68rem',
                                            background: isLatest ? accentBg : undefined,
                                            color: isLatest ? 'var(--ad-acc)' : '#8a90a6',
                                        }}
                                    >
                                        {dmy(d)}
                                    </th>
                                );
                            })}
                            <th
                                style={{
                                    padding: '8px 12px',
                                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                                    textAlign: 'right',
                                    ...mutedLabel,
                                    fontSize: '0.68rem',
                                }}
                            >
                                {t('Δ (zadnje)', 'Δ (latest)')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {overview.lines.map((line, idx) => {
                            const prevSection = idx > 0 ? overview.lines[idx - 1].section : null;
                            const showSection = line.section && line.section !== prevSection;
                            const sectionLabel =
                                line.section && SECTION_LABELS[line.section]
                                    ? locale === 'en'
                                        ? SECTION_LABELS[line.section].en
                                        : SECTION_LABELS[line.section].hr
                                    : line.section;
                            const isGap = line.line_key === 'gap';
                            const rowBg = isGap ? accentBg : line.is_derived ? '#f7f8fa' : undefined;
                            const colSpan = overview.dates.length + 2;
                            return (
                                <React.Fragment key={line.line_key}>
                                    {showSection ? (
                                        <tr>
                                            <td
                                                colSpan={colSpan}
                                                style={{
                                                    padding: '10px 12px 4px',
                                                    ...mutedLabel,
                                                    fontSize: '0.66rem',
                                                    color: 'var(--ad-acc)',
                                                }}
                                            >
                                                {sectionLabel}
                                            </td>
                                        </tr>
                                    ) : null}
                                    <tr style={{ background: rowBg }}>
                                        <td
                                            style={{
                                                padding: '8px 12px',
                                                borderBottom: '1px solid rgba(0,0,0,0.05)',
                                                fontWeight: isGap || line.is_derived ? 700 : 400,
                                                color: isGap ? 'var(--ad-acc)' : 'var(--ad-ink)',
                                                paddingLeft: line.is_derived && !isGap ? 24 : 12,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {line.rbr ? <span style={{ color: '#8a90a6', marginRight: 6 }}>{line.rbr}</span> : null}
                                            {line.description}
                                        </td>
                                        {overview.dates.map((d) => {
                                            const isLatest = d === overview.latest;
                                            const v = line.values[d] ?? null;
                                            return (
                                                <td
                                                    key={d}
                                                    style={{
                                                        padding: '8px 12px',
                                                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                                                        textAlign: 'right',
                                                        fontVariantNumeric: 'tabular-nums',
                                                        background: isLatest ? accentBg : undefined,
                                                        fontWeight: isGap || line.is_derived ? 700 : 400,
                                                        color: v === null ? '#8a90a6' : 'var(--ad-ink)',
                                                    }}
                                                >
                                                    {money(v)}
                                                </td>
                                            );
                                        })}
                                        <td
                                            style={{
                                                padding: '8px 12px',
                                                borderBottom: '1px solid rgba(0,0,0,0.05)',
                                                textAlign: 'right',
                                                fontVariantNumeric: 'tabular-nums',
                                            }}
                                        >
                                            <DeltaCell value={line.delta} />
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* 4) Reconcile panel */}
            <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
                <div style={{ ...mutedLabel, padding: '16px 16px 8px' }}>
                    {t('Usporedba: GAP izvještaj vs BIS auto', 'Reconciliation: GAP report vs BIS auto')}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ textAlign: 'left' }}>
                            {[
                                t('Linija', 'Line'),
                                t('GAP izvještaj', 'GAP report'),
                                t('BIS auto', 'BIS auto'),
                                'Δ',
                                t('Pokrivenost', 'Coverage'),
                            ].map((h, i) => (
                                <th
                                    key={h}
                                    style={{
                                        padding: '8px 12px',
                                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                                        textAlign: i === 0 || i === 4 ? 'left' : 'right',
                                        ...mutedLabel,
                                        fontSize: '0.68rem',
                                    }}
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {overview.reconcile.map((r) => (
                            <tr key={r.line_key}>
                                <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: 'var(--ad-ink)' }}>
                                    {r.label}
                                </td>
                                <td
                                    style={{
                                        padding: '8px 12px',
                                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                                        textAlign: 'right',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: r.report === null ? '#8a90a6' : 'var(--ad-ink)',
                                    }}
                                >
                                    {money(r.report)}
                                </td>
                                <td
                                    style={{
                                        padding: '8px 12px',
                                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                                        textAlign: 'right',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: 'var(--ad-ink)',
                                    }}
                                >
                                    {formatMoney(r.bis, locale)}
                                </td>
                                <td
                                    style={{
                                        padding: '8px 12px',
                                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                                        textAlign: 'right',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {r.report === null ? <span style={{ color: '#8a90a6' }}>—</span> : <DeltaCell value={r.delta} />}
                                </td>
                                <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                    <CoverageBadge coverage={r.coverage} locale={locale} />
                                </td>
                            </tr>
                        ))}
                        {overview.reconcile.length === 0 ? (
                            <tr>
                                <td colSpan={5} style={{ padding: '12px', color: '#8a90a6' }}>
                                    {t('Nema stavki za usporedbu.', 'Nothing to reconcile.')}
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
